import {translate} from "react-i18nify";

import * as formatHelper from "../../helpers/formatHelper";
import * as autotaskTypes from "../../constants/autotaskTypes";

/**
 * @class Autotask
 */
export default class Autotask {
    /**
     * @param {Number}  id
     * @param {String}  name
     * @param {Number}  actionType
     * @param {Number}  startType
     * @param {Boolean} enabled
     * @param {Boolean} canPostpone
     * @param {Number}  postponeInterval
     * @param {Date}    nextRun
     * @param {Number}  runInterval
     * @param {Array}   runMonthDays
     * @param {Array}   runWeekDays
     * @param {Number}  minDuration
     * @param {Number}  volume
     * @param {Array}   formula
     * @param {Number}  threshold
     * @param {Array}   canisters
     * @param {Number}  cleaningTime
     * @param {Array}   channels
     * @param {Number}  recirculationTime
     * @param {Number}  recirculationSpeed
     * @param {Number}  stirringTime
     * @param {Number}  stirringSpeed
     */
    constructor(
        id,
        name,
        actionType,
        startType,
        enabled,
        canPostpone,
        postponeInterval,
        nextRun,
        runInterval,
        runMonthDays,
        runWeekDays,
        minDuration,
        volume,
        formula,
        threshold,
        canisters,
        cleaningTime,
        channels,
        recirculationTime,
        recirculationSpeed,
        stirringTime,
        stirringSpeed
    ) {
        this._id = id;
        this._name = name;
        this._actionType = actionType;
        this._startType = startType;
        this._enabled = enabled;
        this._canPostpone = canPostpone;
        this._postponeInterval = postponeInterval;
        this._nextRun = nextRun;
        this._runInterval = runInterval;
        this._runMonthDays = runMonthDays;
        this._runWeekDays = runWeekDays;
        this._minDuration = minDuration;
        this._volume = volume;
        this._formula = formula;
        this._threshold = threshold;
        this._canisters = canisters;
        this._cleaningTime = cleaningTime;
        this._channels = channels;
        this._recirculationTime = recirculationTime;
        this._recirculationSpeed = recirculationSpeed;
        this._stirringTime = stirringTime;
        this._stirringSpeed = stirringSpeed;

        if (typeof this._id !== "number") {
            throw new Error("Invalid datatype of ID - Autotask");
        }
    }

    /**
     * @return {Number}
     */
    get id() {
        return this._id;
    }

    /**
     * @param {String} name
     */
    set name(name) {
        this._name = name;
    }

    /**
     * @return {String}
     */
    get name() {
        return this._name;
    }

    /**
     * @param {Number} actionType
     */
    set actionType(actionType) {
        this._actionType = actionType;
    }

    /**
     * @return {Number}
     */
    get actionType() {
        return this._actionType;
    }

    /**
     * @param {Number} startType
     */
    set startType(startType) {
        this._startType = startType;
    }

    /**
     * @return {Number}
     */
    get startType() {
        return this._startType;
    }

    /**
     * @param {Boolean} enabled
     */
    set enabled(enabled) {
        this._enabled = enabled;
    }

    /**
     * @return {Boolean}
     */
    get enabled() {
        return this._enabled;
    }

    /**
     * @param {Boolean} canPostpone
     */
    set canPostpone(canPostpone) {
        this._canPostpone = canPostpone;
    }

    /**
     * @return {Boolean}
     */
    get canPostpone() {
        return this._canPostpone;
    }

    /**
     * @param {Number} postponeInterval
     */
    set postponeInterval(postponeInterval) {
        this._postponeInterval = postponeInterval;
    }

    /**
     * @return {Number}
     */
    get postponeInterval() {
        return this._postponeInterval;
    }

    /**
     * @param {Date} nextRun
     */
    set nextRun(nextRun) {
        this._nextRun = nextRun;
    }

    /**
     * @return {Date}
     */
    get nextRun() {
        return this._nextRun;
    }

    /**
     * @param {Number} runInterval
     */
    set runInterval(runInterval) {
        this._runInterval = runInterval;
    }

    /**
     * @return {Number}
     */
    get runInterval() {
        return this._runInterval;
    }

    /**
     * @param {Array} runMonthDays
     */
    set runMonthDays(runMonthDays) {
        this._runMonthDays = runMonthDays;
    }

    /**
     * @return {Array}
     */
    get runMonthDays() {
        return this._runMonthDays;
    }

    /**
     * @param {Array} runWeekDays
     */
    set runWeekDays(runWeekDays) {
        this._runWeekDays = runWeekDays;
    }

    /**
     * @return {Array}
     */
    get runWeekDays() {
        return this._runWeekDays;
    }

    /**
     * @param {Number} id
     */
    set id(id) {
        this._id = id;
    }

    /**
     * @return {Number}
     */
    get minDuration() {
        return this._minDuration;
    }

    /**
     * @param {Number} minDuration
     */
    set minDuration(minDuration) {
        this._minDuration = minDuration;
    }

    /**
     * @return {Number}
     */
    get volume() {
        return this._volume;
    }

    /**
     * @param {Number} volume
     */
    set volume(volume) {
        this._volume = volume;
    }

    /**
     * @return {Array}
     */
    get formula() {
        return this._formula;
    }

    /**
     * @param {Array} formula
     */
    set formula(formula) {
        this._formula = formula;
    }

    /**
     * @param {Number} threshold
     */
    set threshold(threshold) {
        this._threshold = threshold;
    }

    /**
     * @return {Number}
     */
    get threshold() {
        return this._threshold;
    }

    /**
     * @return {Array}
     */
    get canisters() {
        return this._canisters;
    }

    /**
     * @param {Array} canisters
     */
    set canisters(canisters) {
        this._canisters = canisters;
    }

    /**
     * @param {Number} cleaningTime
     */
    set cleaningTime(cleaningTime) {
        this._cleaningTime = cleaningTime;
    }

    /**
     * @return {Number}
     */
    get cleaningTime() {
        return this._cleaningTime;
    }

    /**
     * @param {Array} channels
     */
    set channels(channels) {
        this._channels = channels;
    }

    /**
     * @return {Array}
     */
    get channels() {
        return this._channels;
    }

    /**
     * @param {Number} recirculationTime
     */
    set recirculationTime(recirculationTime) {
        this._recirculationTime = recirculationTime;
    }

    /**
     * @return {Number}
     */
    get recirculationTime() {
        return this._recirculationTime;
    }

    /**
     * @param {Number} recirculationSpeed
     */
    set recirculationSpeed(recirculationSpeed) {
        this._recirculationSpeed = recirculationSpeed;
    }

    /**
     * @return {Number}
     */
    get recirculationSpeed() {
        return this._recirculationSpeed;
    }

    /**
     * @param {Number} stirringTime
     */
    set stirringTime(stirringTime) {
        this._stirringTime = stirringTime;
    }

    /**
     * @return {Number}
     */
    get stirringTime() {
        return this._stirringTime;
    }

    /**
     * @param {Number} stirringSpeed
     */
    set stirringSpeed(stirringSpeed) {
        this._stirringSpeed = stirringSpeed;
    }

    /**
     * @return {Number}
     */
    get stirringSpeed() {
        return this._stirringSpeed;
    }

    /**
     * @return {String}
     */
    formatNextRunDateTime() {
        if (!this._enabled) {
            return translate("machineSettings.disabled");
        }

        if (this._startType === autotaskTypes.START_TYPE_ON_START) {
            return translate("machineSettings.onStart");
        }

        if (this._nextRun) {
            return formatHelper.localizeDateTime(this._nextRun);
        }

        return "";
    }
}
